// Desenha outfits a partir das folhas de sprites originais, pintando cabeça, corpo, pernas e pés
// com as cores escolhidas (mesma tabela de 133 cores do Tibia).
import type { Outfit } from '../../../shared/src/protocol';

interface SheetInfo { sheet: string; first: number; last: number; w: number; h: number; cols: number }
interface Group { fixed: number; pw: number; ph: number; pd: number; layers: number; phases: number; sprites: number[] }
export interface OutfitData { outfits: Record<string, { groups: Group[] }>; sheets: SheetInfo[] }

export const DIR = { north: 0, east: 1, south: 2, west: 3 } as const;

let dataPromise: Promise<OutfitData> | null = null;
export function loadOutfitData(): Promise<OutfitData> {
  dataPromise ??= fetch('/data/outfits.json', { cache: 'no-cache' }).then((r) => {
    if (!r.ok) throw new Error('Não foi possível carregar os outfits.');
    return r.json();
  });
  return dataPromise;
}

const images = new Map<string, Promise<HTMLImageElement>>();
function loadSheet(file: string): Promise<HTMLImageElement> {
  let p = images.get(file);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`folha ${file} não encontrada`));
      // folhas dos outfits custom do Baiak ficam no próprio site (client/public/custom)
      img.src = file.startsWith('custom/') ? '/' + file : '/assets/sheets/' + file;
    });
    images.set(file, p);
  }
  return p;
}

async function drawSprite(ctx: CanvasRenderingContext2D, data: OutfitData, id: number) {
  if (!id) return;
  const s = data.sheets.find((x) => id >= x.first && id <= x.last);
  if (!s) return;
  const img = await loadSheet(s.sheet);
  const i = id - s.first;
  ctx.drawImage(img, (i % s.cols) * s.w, Math.floor(i / s.cols) * s.h, s.w, s.h, 0, 0, s.w, s.h);
}

/** Cor do Tibia (0 a 132) em RGB. */
export function outfitColor(color: number): [number, number, number] {
  const STEPS = 19, VALUES = 7;
  if (color >= STEPS * VALUES) color = 0;
  let h = 0, s = 0, v = 0;
  if (color % STEPS !== 0) {
    h = (color % STEPS) / 18;
    const sv: Array<[number, number]> = [[0.25, 1], [0.25, 0.75], [0.5, 0.75], [0.667, 0.75], [1, 1], [1, 0.75], [1, 0.5]];
    [s, v] = sv[Math.floor(color / STEPS)]!;
  } else {
    v = 1 - color / STEPS / VALUES;
  }
  if (v === 0) return [0, 0, 0];
  if (s === 0) { const g = Math.round(v * 255); return [g, g, g]; }
  let r = 0, g = 0, b = 0;
  if (h < 1 / 6) { r = v; b = v * (1 - s); g = b + (v - b) * 6 * h; }
  else if (h < 2 / 6) { g = v; b = v * (1 - s); r = g - (v - b) * (6 * h - 1); }
  else if (h < 3 / 6) { g = v; r = v * (1 - s); b = r + (v - r) * (6 * h - 2); }
  else if (h < 4 / 6) { b = v; r = v * (1 - s); g = b - (v - r) * (6 * h - 3); }
  else if (h < 5 / 6) { b = v; g = v * (1 - s); r = g + (v - g) * (6 * h - 4); }
  else { r = v; g = v * (1 - s); b = r - (v - g) * (6 * h - 5); }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function newCanvas(w: number, h: number) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

/** Desenha um quadro do outfit (grupo 0 = parado, 1 = andando). */
export async function renderOutfit(outfit: Outfit, dir: number = DIR.south, groupIndex = 0, phase = 0, mounted = false): Promise<HTMLCanvasElement> {
  const data = await loadOutfitData();
  const entry = data.outfits[String(outfit.lookType)];
  if (!entry) throw new Error(`outfit ${outfit.lookType} não carregado`);
  const g = entry.groups[groupIndex] ?? entry.groups[0]!;
  const sheetOf = data.sheets.find((x) => (g.sprites[0] ?? 0) >= x.first && (g.sprites[0] ?? 0) <= x.last);
  // a maioria dos sprites é quadrada (32 ou 64), mas há folhas 32x64 (ex.: Elf Scout): usa largura e altura reais
  const size = sheetOf?.w ?? 64, sizeH = sheetOf?.h ?? size;
  const out = newCanvas(size, sizeH);
  const octx = out.getContext('2d')!;
  const colors = [outfitColor(outfit.head), outfitColor(outfit.body), outfitColor(outfit.legs), outfitColor(outfit.feet)];
  const z = mounted && g.pd > 1 ? 1 : 0;   // padrão "montado" do outfit
  const idx = (layer: number, y: number) => ((((phase % g.phases) * g.pd + z) * g.ph + y) * g.pw + (dir % g.pw)) * g.layers + layer;

  for (let y = 0; y < g.ph; y++) {
    if (y > 0 && !(outfit.addons & (1 << (y - 1)))) continue;
    const base = newCanvas(size, sizeH), bctx = base.getContext('2d', { willReadFrequently: true })!;
    await drawSprite(bctx, data, g.sprites[idx(0, y)] ?? 0);
    if (g.layers > 1) {
      const mask = newCanvas(size, sizeH), mctx = mask.getContext('2d', { willReadFrequently: true })!;
      await drawSprite(mctx, data, g.sprites[idx(1, y)] ?? 0);
      const bp = bctx.getImageData(0, 0, size, sizeH), mp = mctx.getImageData(0, 0, size, sizeH).data;
      const px = bp.data;
      for (let i = 0; i < px.length; i += 4) {
        if (!mp[i + 3]) continue;
        const r = mp[i]!, gg = mp[i + 1]!, b = mp[i + 2]!;
        const part = r && gg && !b ? 0 : r && !gg && !b ? 1 : !r && gg && !b ? 2 : !r && !gg && b ? 3 : -1;
        if (part < 0) continue;
        const c = colors[part]!;
        px[i] = (px[i]! * c[0]) / 255; px[i + 1] = (px[i + 1]! * c[1]) / 255; px[i + 2] = (px[i + 2]! * c[2]) / 255;
      }
      bctx.putImageData(bp, 0, 0);
    }
    octx.drawImage(base, 0, 0);
  }
  return out;
}

/** Quadro do personagem montado: a montaria embaixo e o outfit no padrão montado por cima. */
export async function renderMounted(outfit: Outfit, mountLook: number, dir: number, groupIndex: number, phase: number): Promise<HTMLCanvasElement> {
  const mount = await renderOutfit({ lookType: mountLook, head: 0, body: 0, legs: 0, feet: 0, addons: 0 }, dir, groupIndex, phase);
  const rider = await renderOutfit(outfit, dir, groupIndex, phase, true);
  const w = Math.max(mount.width, rider.width), h = Math.max(mount.height, rider.height);
  const c = newCanvas(w, h), g = c.getContext('2d')!;
  g.drawImage(mount, w - mount.width, h - mount.height);
  g.drawImage(rider, w - rider.width, h - rider.height);
  return c;
}
